import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Box,
  Typography,
  Card,
  CardContent,
  Tooltip,
  Switch,
  Alert,
  CircularProgress,
  Fab,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import InfoIcon from '@mui/icons-material/Info';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ErrorIcon from '@mui/icons-material/Error';
import { ETransportFreightPlutoDto, ETransportFreightPlutoDtoSource, MinimalTarget } from '@/generated/model';
import { FreightDataChange } from '@/utilities/etransportTypes';
import { updateFreight } from '@/generated/client/etransport/etransport';
import { fullDateNoYearNoSeconds } from '@/utilities/dateUtils';
import { useTranslation } from 'react-i18next';
import { EtransportAutomaticIcon, EtransportManualIcon } from './utils/EtransportSvgIcons';
import { getPlateNumber, isClosedTarget } from './utils/utils';
import { useNotificationStore } from '@/stores/notificationstore';
import { ConfirmLeaveDialog } from './ConfirmLeaveDialog';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';

export function TargetFreightsDetail({
  targets,
  freight,
  newChange,
  openTargetFreightSubmitForm,
  onRefetch,
  setNewChanges,
  setCurrentFreight,
  setIsClosingDetails,
  setOpenTargetFreightDetails,
  setOpenTargetFreightSubmitForm,
}: {
  targets: MinimalTarget[] | undefined;
  freight: ETransportFreightPlutoDto | undefined;
  newChange: FreightDataChange | null;
  openTargetFreightSubmitForm: boolean;
  onRefetch: () => void;
  setNewChanges: Dispatch<SetStateAction<FreightDataChange | null>>;
  setCurrentFreight: Dispatch<SetStateAction<ETransportFreightPlutoDto | undefined>>;
  setIsClosingDetails: Dispatch<SetStateAction<boolean>>;
  setOpenTargetFreightDetails: Dispatch<SetStateAction<boolean>>;
  setOpenTargetFreightSubmitForm: Dispatch<SetStateAction<boolean>>;
}) {
  const { t, i18n } = useTranslation();
  const showNotification = useNotificationStore((state) => state.showNotification);
  const [openConfirmLeaveDialog, setOpenConfirmLeaveDialog] = useState<boolean>(false);
  const [startTime] = useState<number>(() => Date.now());
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [checked, setChecked] = useState<boolean>(freight?.isSynchronized || false);
  const [error, setError] = useState<string | null>(null);
  const isAutomatic: boolean = freight?.source === ETransportFreightPlutoDtoSource.AUTOMATIC;
  const isManual: boolean = freight?.source === ETransportFreightPlutoDtoSource.MANUAL;
  const isSyncInProcess: boolean = (freight?.isSynchronized || false) && isManual;

  useEffect(() => {
    setError(null);
  }, [newChange, freight]);

  useEffect(() => {
    setChecked(freight?.isSynchronized || false);
  }, [freight]);

  async function handleSave(): Promise<void> {
    setIsSaving(true);

    const updatedFreight: ETransportFreightPlutoDto = {
      ...freight,
      ...(newChange?.targetFreights && {
        targetFreights: [...(freight?.targetFreights || []), newChange.targetFreights],
      }),
      ...(newChange?.isSyncAdded !== undefined && {
        isSynchronized: newChange.isSyncAdded,
      }),
    };

    try {
      await updateFreight(updatedFreight);
      setCurrentFreight(updatedFreight);
      showNotification('success', t('etransport.targetform.success'), undefined, 10);
      setNewChanges(null);
      onRefetch();
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(t('etransport.freightfrom.apierror'));
    } finally {
      setIsSaving(false);
    }
  }

  function handleOpen(): void {
    setOpenTargetFreightSubmitForm(true);
  }

  function handleClose(): void {
    setOpenTargetFreightSubmitForm(false);
    setOpenTargetFreightDetails(false);
    handleCloseAnimation();
  }

  function handleLeave(): void {
    if (showSaveButton) {
      setOpenConfirmLeaveDialog(true);
    } else {
      handleClose();
    }
  }

  function handleCloseAnimation(): void {
    setIsClosingDetails(true);
    setTimeout(() => {
      setIsClosingDetails(false);
      handleRollbackAllChanges();
    }, 400);
  }

  function handleRollbackAllChanges(): void {
    setChecked(false);
    setNewChanges(null);
  }

  function handleSyncSwitchState(event: React.ChangeEvent<HTMLInputElement>): void {
    const newState: boolean = event.target.checked;
    setNewChanges((prev) => ({
      ...prev,
      isSyncAdded: newState,
    }));
    setChecked(newState);
  }

  function calculateBottomPadding(show: boolean): string {
    let px: number = 0;
    px = px + (show ? (checked ? 180 : 135) : 75);
    const result: string = px + 'px';
    return result;
  }

  const showSaveButton: boolean = useMemo(() => {
    if (!newChange) return false;

    const hasTargetChanges = newChange.targetFreights !== undefined;
    const hasSyncChange = newChange.isSyncAdded === true;

    return hasTargetChanges || hasSyncChange;
  }, [newChange]);

  if (!freight) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: isPhoneOrHighScreen() ? '100%' : '100vh',
      }}
    >
      <div className={`topBar ${isPhoneOrHighScreen() ? 'phone' : ''}`}>
        <Box width="100%" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box width="100%" sx={{ display: 'flex', justifyContent: 'left', alignItems: 'center', gap: 1 }}>
            <ArrowBackIcon
              sx={{ fontSize: '1.5rem', color: 'text.secondary', cursor: 'pointer' }}
              onClick={handleLeave}
            />
            <Typography variant="h6" sx={{ color: 'black' }}>
              {t('etransport.targetdetails.freight.info')}
            </Typography>
          </Box>
          {isAutomatic && (
            <Tooltip title={t('etransport.card.automatic')} arrow>
              <EtransportAutomaticIcon color="grey" backgroundColor="white" />
            </Tooltip>
          )}
          {isManual && !isSyncInProcess && (
            <Tooltip title={t('etransport.card.manual')} arrow>
              <EtransportManualIcon color="white" backgroundColor="var(--mui-palette-primary-main)" />
            </Tooltip>
          )}
          {isManual && isSyncInProcess && (
            <Tooltip title={t('etransport.card.syncinprocess')} arrow>
              <ErrorIcon color="warning" />
            </Tooltip>
          )}
        </Box>
      </div>
      {isSyncInProcess && (
        <Alert severity="warning">
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1}>
            <Typography variant="caption" color="text.secondary">
              {t('etransport.targetdetails.syncinfo')}
            </Typography>
            <Tooltip title={t('etransport.targetdetails.syncwarning')} arrow>
              <InfoIcon sx={{ fontSize: '1rem', color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {showSaveButton && !error && <Alert severity="warning">{t('etransport.targetdetails.savewarning')}</Alert>}
      <div
        className="panelOverlay"
        style={{
          paddingBottom: calculateBottomPadding(showSaveButton),
          marginLeft: isPhoneOrHighScreen() ? '1%' : '0',
          marginRight: isPhoneOrHighScreen() ? '1%' : '0',
        }}
      >
        <Stack spacing={1}>
          <Typography variant="body1" color="text.secondary">
            {t('etransport.targetdetails.freight.data')}
          </Typography>
          <Card
            elevation={3}
            sx={{
              padding: '1px',
              backgroundColor: 'white',
            }}
          >
            <CardContent>
              <Stack spacing={0.1}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <ApartmentIcon sx={{ color: 'var(--mui-palette-primary-main)', fontSize: '0.8rem' }} color="action" />
                  <Typography variant="body2">{`${t('etransport.freightfrom.uit')} (UIT)`}</Typography>
                </Box>
                {freight.uit && (
                  <Box display="flex" alignItems="center" sx={{ pb: 2, color: 'var(--mui-palette-primary-main)' }}>
                    <Typography variant="h6">{freight.uit}</Typography>
                  </Box>
                )}

                <Box display="flex" alignItems="center" gap={0.5}>
                  <ApartmentIcon sx={{ color: 'var(--mui-palette-primary-main)', fontSize: '0.8rem' }} color="action" />
                  <Typography variant="body2">{`${t('etransport.freightfrom.vat')} (VAT)`}</Typography>
                </Box>
                {freight.vat && (
                  <Box display="flex" alignItems="center" sx={{ pb: 2, color: 'var(--mui-palette-primary-main)' }}>
                    <Typography variant="h6">{freight.vat}</Typography>
                  </Box>
                )}

                <Box display="flex" alignItems="center" gap={0.5}>
                  <StickyNote2Icon
                    sx={{ color: 'var(--mui-palette-primary-main)', fontSize: '0.8rem' }}
                    color="action"
                  />
                  <Typography variant="body2">{t('etransport.freightfrom.partner')}</Typography>
                </Box>
                {freight.freightData?.declarantName ? (
                  <Box display="flex" alignItems="center">
                    <Typography variant="h6" color="textSecondary">
                      {freight.freightData?.declarantName}
                    </Typography>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center">
                    <Typography variant="h6" color="textSecondary">
                      {t('etransport.targetdetails.nodata')}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
          <Typography variant="body1" color="text.secondary">
            {t('etransport.targetdetails.freight.sync')}
          </Typography>
          <Card elevation={3} sx={{ padding: '1px', backgroundColor: 'white' }}>
            <CardContent>
              <Stack spacing={0.1}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <AccessTimeIcon sx={{ color: 'var(--mui-palette-primary-main)', fontSize: '0.8rem' }} />
                  <Typography variant="body2">{t('etransport.targetdetails.valid')}</Typography>
                </Box>
                {freight.validityStartTimeMicros && freight.validityEndTimeMicros && (
                  <Box display="flex" alignItems="center" sx={{ pb: 2, color: 'black' }}>
                    <Typography variant="body2" color="text.secondary">
                      {fullDateNoYearNoSeconds(freight.validityStartTimeMicros || 0, i18n.language, t)} {' - '}
                      {fullDateNoYearNoSeconds(freight.validityEndTimeMicros || 0, i18n.language, t)}
                    </Typography>
                  </Box>
                )}

                <Box display="flex" alignItems="center" gap={0.5}>
                  <CloudUploadIcon sx={{ color: 'var(--mui-palette-primary-main)', fontSize: '1rem' }} />
                  <Typography variant="body2">{t('etransport.targetdetails.gps')}</Typography>
                </Box>
                {freight.lastSentTimeMicros !== undefined && freight.lastSentTimeMicros > 0 ? (
                  <Box display="flex" alignItems="center" sx={{ pb: 2, color: 'black' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('etransport.targetdetails.toanaf2')} '
                      {fullDateNoYearNoSeconds(freight.lastSentTimeMicros || 0, i18n.language, t)}'{' '}
                      {t('etransport.targetdetails.toanaf')}
                    </Typography>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" sx={{ pb: 2, color: 'black' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('etransport.targetdetails.toanafnosync')}
                    </Typography>
                  </Box>
                )}

                <Box display="flex" alignItems="center" gap={0.5}>
                  <CloudDownloadIcon sx={{ color: 'var(--mui-palette-primary-main)', fontSize: '1rem' }} />
                  <Typography variant="body2">{t('etransport.targetdetails.anaf')}</Typography>
                </Box>
                {freight?.source === ETransportFreightPlutoDtoSource.AUTOMATIC ? (
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('etransport.targetdetails.fromanaf')}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        {t('etransport.targetdetails.fromanafno')}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        Sync
                      </Typography>
                      <Switch
                        size="medium"
                        checked={checked}
                        onChange={handleSyncSwitchState}
                        disabled={isSyncInProcess || isClosedTarget(startTime, freight?.validityEndTimeMicros)}
                        sx={{
                          transform: 'scale(0.8)',
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: isClosedTarget(startTime, freight?.validityEndTimeMicros)
                              ? 'grey'
                              : 'var(--mui-palette-secondary-main)',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: isClosedTarget(startTime, freight?.validityEndTimeMicros)
                              ? 'white'
                              : 'var(--mui-palette-secondary-dark)',
                          },
                        }}
                      />
                      <Tooltip title={t('etransport.card.syncinfo')} arrow>
                        <InfoIcon sx={{ fontSize: '1.1rem', color: 'text.secondary', cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
          <Typography variant="body1" color="text.secondary">
            {t('etransport.targetdetails.freight.history')}
          </Typography>

          {openTargetFreightSubmitForm && (
            <Card
              elevation={0}
              sx={{
                backgroundColor: '#ffffff00', //to be transparent
                border: 'dashed var(--mui-palette-primary-main) 2px',
              }}
            >
              <CardContent>
                <Box sx={{ position: 'relative', padding: '20px' }}></Box>
              </CardContent>
            </Card>
          )}

          {newChange?.targetFreights !== undefined && (
            <Card
              elevation={3}
              sx={{
                backgroundColor: 'rgb(248, 242, 236)',
              }}
            >
              <CardContent>
                <Box sx={{ position: 'relative' }}>
                  <Stack spacing={0}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocalShippingOutlinedIcon sx={{ color: 'var(--mui-palette-primary-main)', fontSize: '1rem' }} />
                      <Typography variant="body2">{t('etransport.freightfrom.plate')}:</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getPlateNumber(targets, newChange?.targetFreights.targetId || 0)}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                      <LocalShippingIcon sx={{ color: 'var(--mui-palette-primary-main)', fontSize: '1rem' }} />
                      <Typography variant="body2">{t('etransport.targetdetails.trailers')}:</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {newChange?.targetFreights.trailer1 || 'N/A'}
                        {newChange?.targetFreights.trailer2 && `, ${newChange?.targetFreights.trailer2}`};
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                      <AccessTimeIcon sx={{ color: 'var(--mui-palette-primary-main)', fontSize: '0.8rem' }} />
                      <Typography variant="body2">{t('etransport.freightfrom.validitystart')}:</Typography>
                      <Typography variant="body2" color="text.secondary" display="block">
                        {fullDateNoYearNoSeconds(newChange?.targetFreights.startTimeMicros || 0, i18n.language, t)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          )}

          {freight.targetFreights &&
            freight.targetFreights
              .slice()
              .sort((a, b) => (b.startTimeMicros || 0) - (a.startTimeMicros || 0))
              .map((tf, index) => {
                return (
                  <Card
                    key={index}
                    elevation={3}
                    sx={{
                      backgroundColor: 'white',
                    }}
                  >
                    <CardContent>
                      <Box key={index} sx={{ position: 'relative' }}>
                        <Stack spacing={0}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <LocalShippingOutlinedIcon
                              sx={{ color: 'var(--mui-palette-primary-main)', fontSize: '1rem' }}
                            />
                            <Typography variant="body2">{t('etransport.freightfrom.plate')}:</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {getPlateNumber(targets, tf.targetId || 0)}
                            </Typography>
                          </Box>

                          <Box display="flex" alignItems="center" gap={1}>
                            <LocalShippingIcon sx={{ color: 'var(--mui-palette-primary-main)', fontSize: '1rem' }} />
                            <Typography variant="body2">{t('etransport.targetdetails.trailers')}:</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {tf.trailer1 || 'N/A'}
                              {tf.trailer2 && `, ${tf.trailer2}`}
                            </Typography>
                          </Box>

                          <Box display="flex" alignItems="center" gap={1}>
                            <AccessTimeIcon sx={{ color: 'var(--mui-palette-primary-main)', fontSize: '0.8rem' }} />
                            <Typography variant="body2">{t('etransport.freightfrom.validitystart')}:</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {fullDateNoYearNoSeconds(tf.startTimeMicros || 0, i18n.language, t)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
        </Stack>
      </div>

      <Stack
        spacing={1}
        sx={{
          position: 'absolute',
          bottom: isPhoneOrHighScreen() ? 5 : 20,
          right: isPhoneOrHighScreen() ? 10 : 18,
        }}
      >
        {isManual && newChange?.targetFreights === undefined && (
          <Fab
            variant="extended"
            onClick={handleOpen}
            aria-label={t('etransport.freightfrom.change')}
            disabled={isSyncInProcess || isClosedTarget(startTime, freight?.validityEndTimeMicros)}
            sx={{
              boxShadow: 6,
              backgroundColor: 'var(--mui-palette-primary-main)',
              color: 'white',
              border: '2px solid white',
              '&:hover': {
                backgroundColor: 'var(--mui-palette-primary-light)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'grey',
                color: 'white',
                opacity: 1,
              },
            }}
          >
            <AirportShuttleIcon sx={{ mr: 1 }} />
            {t('etransport.freightfrom.change')}
          </Fab>
        )}
        {isManual && showSaveButton && (
          <>
            <Fab
              variant="extended"
              disabled={isSaving}
              onClick={handleSave}
              aria-label={t('etransport.freightfrom.save')}
              sx={{
                boxShadow: 6,
                backgroundColor: 'var(--mui-palette-secondary-main)',
                color: 'white',
                border: '2px solid white',
                '&:hover': {
                  backgroundColor: 'var(--mui-palette-secondary-light)',
                },
              }}
            >
              {isSaving ? <CircularProgress size={20} /> : <CheckCircleOutlineIcon sx={{ mr: 1 }} />}
              {t('etransport.freightfrom.save')}
            </Fab>
            <Fab
              variant="extended"
              onClick={handleRollbackAllChanges}
              aria-label={t('etransport.freightfrom.cancel')}
              sx={{
                boxShadow: 6,
                backgroundColor: 'white',
                color: 'var(--mui-palette-secondary-main)',
                border: '2px solid var(--mui-palette-secondary-main)',
                '&:hover': {
                  backgroundColor: 'rgba(146, 102, 21, 0.1)',
                },
              }}
            >
              <HighlightOffOutlinedIcon sx={{ mr: 1 }} />
              {t('etransport.freightfrom.cancel')}
            </Fab>
          </>
        )}
      </Stack>

      <ConfirmLeaveDialog
        uit={freight.uit}
        open={openConfirmLeaveDialog}
        setOpenConfirmDialog={setOpenConfirmLeaveDialog}
        handleCloseDialog={handleClose}
      />
    </Box>
  );
}
